import React, { createContext, useContext, useState, useCallback } from 'react';
import styled, { keyframes } from 'styled-components';

const ToastContext = createContext();

export const useToast = () => useContext(ToastContext);

const slideIn = keyframes`
  from { transform: translateX(100%); opacity: 0; }
  to { transform: translateX(0); opacity: 1; }
`;

const slideOut = keyframes`
  from { transform: translateX(0); opacity: 1; }
  to { transform: translateX(100%); opacity: 0; }
`;

const ToastContainer = styled.div`
  position: fixed;
  top: 90px;
  right: 20px;
  z-index: 10000;
  display: flex;
  flex-direction: column;
  gap: 8px;
  pointer-events: none;

  @media (max-width: 480px) {
    right: 10px;
    left: 10px;
  }
`;

const ToastItem = styled.div`
  background: ${props => {
    switch (props.$type) {
      case 'success': return 'var(--success-bg)';
      case 'error': return 'var(--error-bg)';
      default: return 'var(--bg-elevated)';
    }
  }};
  color: ${props => {
    switch (props.$type) {
      case 'success': return 'var(--success-text)';
      case 'error': return 'var(--error-text)';
      default: return 'var(--text-primary)';
    }
  }};
  border: 1px solid ${props => {
    switch (props.$type) {
      case 'success': return 'var(--success-border)';
      case 'error': return 'var(--error-border)';
      default: return 'var(--border-primary)';
    }
  }};
  padding: 12px 20px;
  border-radius: 10px;
  box-shadow: 0 4px 20px var(--shadow-strong);
  font-size: 14px;
  font-weight: 500;
  pointer-events: auto;
  animation: ${props => props.$exiting ? slideOut : slideIn} 0.3s ease forwards;
  max-width: 360px;

  @media (max-width: 480px) {
    max-width: 100%;
  }
`;

let toastId = 0;

export const ToastProvider = ({ children }) => {
  const [toasts, setToasts] = useState([]);

  const removeToast = useCallback((id) => {
    setToasts(prev => prev.map(t => t.id === id ? { ...t, exiting: true } : t));
    setTimeout(() => setToasts(prev => prev.filter(t => t.id !== id)), 300);
  }, []);

  const toast = useCallback((message, type = 'info', duration = 3000) => {
    const id = ++toastId;
    setToasts(prev => [...prev, { id, message, type, exiting: false }]);
    setTimeout(() => removeToast(id), duration);
  }, [removeToast]);

  return (
    <ToastContext.Provider value={{ toast, success: (msg) => toast(msg, 'success'), error: (msg) => toast(msg, 'error') }}>
      {children}
      <ToastContainer>
        {toasts.map(t => (
          <ToastItem key={t.id} $type={t.type} $exiting={t.exiting} role="status" aria-live="polite">
            {t.message}
          </ToastItem>
        ))}
      </ToastContainer>
    </ToastContext.Provider>
  );
};
